import { useState, type ReactNode } from 'react';
import styled from 'styled-components';
import type { PtoRdEditsReason } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input, Select, TextArea } from '@shared/ui/Input';
import { Modal } from '@shared/ui/Modal';
import type { SheetForm, ValueForm } from './logic';

/** Общие части окон правок РД (№8): поля значения и листа, причина, ошибки,
 *  окно «укажите причину». */

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export const Grid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols = 3 }) => $cols}, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing(2.5)};

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const FieldLabel = styled.label<{ $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  ${({ $wide }) => ($wide ? 'grid-column: 1 / -1;' : '')}

  input,
  select,
  textarea {
    font-weight: 400;
  }
`;

export const CheckLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13.5px;
  line-height: 1.45;
  cursor: pointer;

  input {
    margin-top: 3px;
  }
`;

export const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  line-height: 1.5;
`;

export const Hint = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  margin-top: 2px;
  line-height: 1.45;
`;

export const Section = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  line-height: 1.5;
`;

/** Предупреждение (привязка КСП, строка не найдена в тексте листа). */
export const Warn = styled.div`
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.warning}1A;
  border: 1px solid ${({ theme }) => theme.colors.status.warning}55;
  font-size: 13px;
  line-height: 1.5;
`;

export const Done = styled.div`
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.success}1A;
  border: 1px solid ${({ theme }) => theme.colors.status.success}33;
  font-size: 13.5px;
  line-height: 1.5;
`;

export const ErrorText = styled.div.attrs({ role: 'alert' })`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.status.danger};
`;

export const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
`;

/** Строка «было → стало». */
export const Diff = styled.div`
  font-size: 12.5px;
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

/** Ошибки проверки формы — первые три (остальные появятся после исправления). */
export function Errors({ list }: { list: readonly string[] }) {
  if (list.length === 0) return null;
  return (
    <ErrorText>
      {list.slice(0, 3).map((e) => (
        <div key={e}>{e}</div>
      ))}
    </ErrorText>
  );
}

/** Поля значения строки: наименование, единица, количество, шифр, обозначение,
 *  масса единицы. `lockQtyUnit` — привязка КСП: количество и единица не
 *  правятся. */
export function ValueFields({
  value,
  onChange,
  lockQtyUnit = false,
  compact = false,
  idPrefix,
}: {
  value: ValueForm;
  onChange: (next: ValueForm) => void;
  lockQtyUnit?: boolean;
  /** Без шифра и массы (части разделённой строки). */
  compact?: boolean;
  idPrefix: string;
}) {
  const set = (k: keyof ValueForm) => (e: { target: { value: string } }) =>
    onChange({ ...value, [k]: e.target.value });
  return (
    <Grid $cols={3}>
      <FieldLabel $wide htmlFor={`${idPrefix}-name`}>
        Наименование
        <Input id={`${idPrefix}-name`} value={value.name} onChange={set('name')} maxLength={1000} />
      </FieldLabel>
      <FieldLabel htmlFor={`${idPrefix}-qty`}>
        Количество
        <Input
          id={`${idPrefix}-qty`}
          inputMode="decimal"
          value={value.quantity}
          onChange={set('quantity')}
          disabled={lockQtyUnit}
          placeholder="например, 1 445,5"
        />
      </FieldLabel>
      <FieldLabel htmlFor={`${idPrefix}-unit`}>
        Ед. изм.
        <Input
          id={`${idPrefix}-unit`}
          value={value.unit}
          onChange={set('unit')}
          disabled={lockQtyUnit}
          maxLength={40}
        />
      </FieldLabel>
      <FieldLabel htmlFor={`${idPrefix}-designation`}>
        Обозначение, марка
        <Input
          id={`${idPrefix}-designation`}
          value={value.designation}
          onChange={set('designation')}
          maxLength={300}
        />
      </FieldLabel>
      {!compact && (
        <>
          <FieldLabel htmlFor={`${idPrefix}-code`}>
            Шифр (код продукции, расценки)
            <Input
              id={`${idPrefix}-code`}
              value={value.code}
              onChange={set('code')}
              maxLength={300}
            />
          </FieldLabel>
          <FieldLabel htmlFor={`${idPrefix}-mass`}>
            Масса единицы, кг
            <Input
              id={`${idPrefix}-mass`}
              inputMode="decimal"
              value={value.unitMass}
              onChange={set('unitMass')}
            />
          </FieldLabel>
        </>
      )}
    </Grid>
  );
}

/** Лист и строка РД: страница PDF комплекта, лист по основной надписи, строка
 *  или позиция на листе. `rowLabel` — подпись поля строки. */
export function SheetFields({
  sheet,
  onChange,
  totalPages,
  rowLabel = 'Строка на листе (позиция или текст строки)',
  pageLocked = false,
  idPrefix,
}: {
  sheet: SheetForm;
  onChange: (next: SheetForm) => void;
  totalPages: number | null;
  rowLabel?: string;
  /** Страница не меняется (ручная строка — после строки того же листа). */
  pageLocked?: boolean;
  idPrefix: string;
}) {
  const set = (k: keyof SheetForm) => (e: { target: { value: string } }) =>
    onChange({ ...sheet, [k]: e.target.value });
  return (
    <Grid $cols={3}>
      <FieldLabel htmlFor={`${idPrefix}-page`}>
        Стр. PDF комплекта{totalPages ? ` (из ${totalPages})` : ''}
        <Input
          id={`${idPrefix}-page`}
          inputMode="numeric"
          value={sheet.page}
          onChange={set('page')}
          disabled={pageLocked}
        />
      </FieldLabel>
      <FieldLabel htmlFor={`${idPrefix}-sheet`}>
        Лист по основной надписи
        <Input
          id={`${idPrefix}-sheet`}
          value={sheet.sheet}
          onChange={set('sheet')}
          placeholder="СП01 л. 2"
          maxLength={120}
        />
      </FieldLabel>
      <FieldLabel htmlFor={`${idPrefix}-row`}>
        {rowLabel}
        <Input
          id={`${idPrefix}-row`}
          value={sheet.row}
          onChange={set('row')}
          placeholder="поз. 5"
          maxLength={300}
        />
      </FieldLabel>
    </Grid>
  );
}

/** Причина из справочника. */
export function ReasonSelect({
  value,
  onChange,
  reasons,
  id,
  label = 'Причина',
}: {
  value: string;
  onChange: (code: string) => void;
  reasons: readonly PtoRdEditsReason[];
  id: string;
  label?: string;
}) {
  return (
    <FieldLabel htmlFor={id}>
      {label}
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— выберите из списка —</option>
        {reasons.map((r) => (
          <option key={r.code} value={r.code}>
            {r.label}
          </option>
        ))}
      </Select>
    </FieldLabel>
  );
}

/** Комментарий. */
export function CommentField({
  value,
  onChange,
  id,
  label = 'Комментарий (необязательно)',
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  id: string;
  label?: string;
  placeholder?: string;
}) {
  return (
    <FieldLabel htmlFor={id}>
      {label}
      <TextArea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={4000}
        placeholder={placeholder}
        style={{ minHeight: 64 }}
      />
    </FieldLabel>
  );
}

/** Окно «укажите причину»: отозвать, отменить, не подтверждать. Без причины
 *  не отправляется. */
export function ReasonDialog({
  title,
  warning,
  confirmLabel,
  busy,
  error,
  onConfirm,
  onClose,
  placeholder,
}: {
  title: string;
  warning?: ReactNode;
  confirmLabel: string;
  busy: boolean;
  error: string | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  placeholder?: string;
}) {
  const [reason, setReason] = useState('');
  const empty = !reason.trim();
  return (
    <Modal open title={title} onClose={busy ? () => undefined : onClose}>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (!empty) onConfirm(reason.trim());
        }}
      >
        {warning && <Warn>{warning}</Warn>}
        <FieldLabel htmlFor="rd-edit-reason">
          Причина (обязательно)
          <TextArea
            id="rd-edit-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={2000}
            placeholder={placeholder}
            autoFocus
            style={{ minHeight: 72 }}
          />
        </FieldLabel>
        {error && <ErrorText>{error}</ErrorText>}
        <Buttons>
          <Button type="submit" $variant="primary" disabled={busy || empty}>
            {busy ? 'Сохранение…' : confirmLabel}
          </Button>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
        </Buttons>
      </Form>
    </Modal>
  );
}

/** Сохранить Blob файлом. */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
